import { useAuth } from "@assetwatch/auth";
import {
  Alert,
  Avatar,
  Button,
  Chip,
  Divider,
  Group,
  Icon,
  IconButton,
  Input,
  Label,
  MenuButton,
  type MenuButtonItem,
  Select,
  Stack,
  Text,
  Textarea,
  Tile,
  Title,
  toast,
} from "@assetwatch/ui";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  canAccessModule,
  canAccessWebNavigationModule,
  canPerformAction,
  type WorkAccessProfile,
} from "../../../lib/access";
import { toOriginSearch } from "../../../lib/navigationOrigin";
import {
  getWorkRequestDeepLink,
  type RequestsPageView,
} from "../../../lib/requestsPageView";
import type { AssetId, WorkRequestId } from "../../../lib/workIds";
import { getWorkScopeSearch } from "../../../lib/workSearch";
import {
  useCancelWorkRequestMutation,
  useDeclineWorkRequestMutation,
  useUpdateWorkRequestMutation,
} from "../../../mutations/useWorkRequestMutations";
import type { RequestPriority, WorkRequest } from "../../../types/workRequest";
import type { DuplicateAnalysis } from "../../../types/workRequestApi";
import { useWorkScope } from "../../WorkScopeProvider";
import {
  displayWorkOrderNumber,
  workOrderStatusTone,
} from "../work-orders/presentation";
import { CancelRequestDialog } from "./CancelRequestDialog";
import {
  DeclineRequestDialog,
  type DeclineRequestDialogInput,
} from "./DeclineRequestDialog";
import { RequestAssetPicker } from "./RequestAssetPicker";
import { RequestAttachments } from "./RequestAttachments";

type RequestDetailsProps = {
  canReview?: boolean;
  request?: WorkRequest;
  onApprove?: (requestId: WorkRequestId) => void;
  onDelete?: (requestId: WorkRequestId) => void;
  onReliInsight?: (requestId: WorkRequestId, hasInsight: boolean) => void;
  reliAnalysis?: DuplicateAnalysis;
  reliAnalysisRequestId?: WorkRequestId;
  reliAnalysisPending?: boolean;
  /** The Requests list view, restored by back links from other modules. */
  originListSearch?: RequestsPageView;
};

function DetailField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Stack gap={8}>
      <Label size="sm" tone="subtle">
        {label}
      </Label>
      {children}
    </Stack>
  );
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] ?? "U"}${parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : ""}`.toUpperCase();
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatCompactDateTime(value: string) {
  const date = new Date(value);
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  const time = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return sameDay ? `Today \u00b7 ${time}` : formatDateTime(value);
}

function getStatusDisplay(request: Pick<WorkRequest, "status">) {
  return {
    label: request.status.name,
    tone:
      request.status.code === "APPROVED"
        ? ("positive" as const)
        : request.status.code === "PENDING"
          ? ("warning" as const)
          : request.status.code === "CANCELED"
            ? ("neutral" as const)
            : ("negative" as const),
  };
}

function PersonMetadata({ label, name }: { label: string; name: string }) {
  return (
    <Group gap={8} wrap="wrap">
      <Text as="span" size="sm" tone="subtle">
        {label}
      </Text>
      <Avatar initials={getInitials(name)} size="xs" variant="internal" />
      <Text as="span" size="sm">
        {name}
      </Text>
    </Group>
  );
}

export function RequestDetails({
  canReview = false,
  request,
  onApprove,
  onDelete,
  originListSearch,
}: RequestDetailsProps) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const { user } = useAuth();
  const cancelMutation = useCancelWorkRequestMutation();
  const declineMutation = useDeclineWorkRequestMutation();
  const updateMutation = useUpdateWorkRequestMutation();
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priorityCode, setPriorityCode] = useState<RequestPriority | null>(
    null,
  );
  const [assetId, setAssetId] = useState<AssetId | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [declineError, setDeclineError] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  if (!request) {
    return (
      <section
        aria-label="Request details"
        className="grid h-full min-h-0 place-items-center rounded-(--radius-lg) border border-outline-subtle bg-surface"
      >
        <Text tone="subtle">Select a request to review its details.</Text>
      </section>
    );
  }

  const selectedRequest = request;
  const statusDisplay = getStatusDisplay(selectedRequest);
  // `selectedFacility` already carries the viewer's Work role and is the
  // capability matrix's `WorkAccessProfile` shape — reuse it rather than
  // reconstructing permission flags ad hoc per call site.
  const accessProfile: WorkAccessProfile | null = selectedFacility;
  const canAccessWorkOrders = accessProfile
    ? canAccessModule(accessProfile, "WORK_ORDERS")
    : false;
  // Links into other modules keep the facility scope of the current page;
  // null until that scope is known.
  const scopeSearch =
    selectedCustomer && selectedFacility
      ? getWorkScopeSearch(selectedCustomer, selectedFacility)
      : null;
  // Links into other modules carry this request as their origin, so the
  // destination shows a back link to it.
  const requestOriginSearch = toOriginSearch({
    type: "request",
    id: selectedRequest.id,
    search: originListSearch,
  });
  const canLinkAsset = Boolean(
    accessProfile &&
      scopeSearch !== null &&
      canAccessWebNavigationModule(accessProfile, "ASSETS"),
  );
  const canEditOwnRequest = accessProfile
    ? canPerformAction(accessProfile, "EDIT_OWN_REQUEST")
    : false;
  const canEditAnyRequest = accessProfile
    ? canPerformAction(accessProfile, "EDIT_ANY_REQUEST")
    : false;
  const canCancelOwnRequest = accessProfile
    ? canPerformAction(accessProfile, "CANCEL_OWN_REQUEST")
    : false;
  const workOrderNumberLabel = selectedRequest.workOrder
    ? displayWorkOrderNumber(selectedRequest.workOrder.number)
    : "";
  const workOrderStatusCode = selectedRequest.workOrder?.status.code ?? "";
  const workOrderUpdatedAt = selectedRequest.workOrder?.updatedAt ?? "";
  const isPending = selectedRequest.status.code === "PENDING";
  const isDeclined = selectedRequest.status.code === "DECLINED";
  const isReviewable = isPending;
  const canApproveDeclinedRequest = Boolean(
    canReview && onApprove && isDeclined && !selectedRequest.hasLinkedWorkOrder,
  );
  const isOwnRequest =
    selectedRequest.requester.cognitoId !== null &&
    selectedRequest.requester.cognitoId === user?.sub;
  const canEditThisRequest =
    canEditAnyRequest || (canEditOwnRequest && isOwnRequest);
  const canCancelThisRequest = canCancelOwnRequest && isOwnRequest;
  const reviewerName = selectedRequest.reviewer?.name;
  const lastUpdatedBy =
    selectedRequest.lastUpdatedBy?.name ??
    selectedRequest.reviewer?.name ??
    selectedRequest.requester.name;
  // View mode hides empty sections instead of rendering placeholders; edit
  // mode always shows every editable field so it can be filled in.
  const viewLocationName = selectedRequest.asset?.locationName || null;
  const viewDescription = selectedRequest.description?.trim() || null;
  // A location only ever comes from the asset, so no asset means neither.
  const showLocationAssetSection = isEditing || selectedRequest.asset !== null;
  const showDescriptionSection = isEditing || viewDescription !== null;
  function openDeclineConfirmation() {
    setDeclineError(null);
    setIsDeclineOpen(true);
  }

  function handleDeclineOpenChange(open: boolean) {
    if (!open && !declineMutation.isPending) {
      setDeclineError(null);
    }
    setIsDeclineOpen(open);
  }

  async function handleDecline(input: DeclineRequestDialogInput) {
    try {
      await declineMutation.mutateAsync({
        ...input,
        requestNumber: selectedRequest.requestNumber,
        workRequestId: selectedRequest.id,
      });
      setDeclineError(null);
      setIsDeclineOpen(false);
    } catch (error) {
      setDeclineError(
        error instanceof Error
          ? error.message
          : "Unable to decline this request. Please try again.",
      );
    }
  }

  function openCancelConfirmation() {
    setCancelError(null);
    setIsCancelOpen(true);
  }

  function handleCancelOpenChange(open: boolean) {
    if (!open && !cancelMutation.isPending) {
      setCancelError(null);
    }
    setIsCancelOpen(open);
  }

  async function handleCancel() {
    try {
      await cancelMutation.mutateAsync({
        requestNumber: selectedRequest.requestNumber,
        workRequestId: selectedRequest.id,
      });
      setCancelError(null);
      setIsCancelOpen(false);
    } catch (error) {
      setCancelError(
        error instanceof Error
          ? error.message
          : "Unable to cancel this request. Please try again.",
      );
    }
  }
  function beginEdit() {
    setTitle(selectedRequest.title);
    setDescription(selectedRequest.description ?? "");
    setPriorityCode(selectedRequest.priority?.code ?? null);
    setAssetId(selectedRequest.asset?.publicId ?? null);
    setSubmissionError(null);
    setIsEditing(true);
  }

  async function handleCopyLink() {
    const url = getWorkRequestDeepLink(
      window.location.href,
      selectedRequest.id,
    );
    if (url === null) {
      toast.error("Unable to copy the request link.", { icon: true });
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.add("Request link copied.", { icon: true });
    } catch {
      toast.error("Unable to copy the request link.", { icon: true });
    }
  }

  async function handleUpdate() {
    try {
      await updateMutation.mutateAsync({
        assetId,
        description: description.trim() || null,
        priorityCode,
        title: title.trim(),
        requestNumber: selectedRequest.requestNumber,
        workRequestId: selectedRequest.id,
      });
      setSubmissionError(null);
      setIsEditing(false);
    } catch (error) {
      setSubmissionError(
        error instanceof Error
          ? error.message
          : "Unable to update this request. Please try again.",
      );
    }
  }

  // Cancel lives in the action row below the request details, so it is
  // deliberately not offered in the overflow menu.
  // "Copy request link" is always present, so the menu is never empty.
  const menuItems: MenuButtonItem[] = [
    {
      id: "copy-link",
      label: "Copy request link",
      leadingIcon: "assign-link",
      onSelect: () => void handleCopyLink(),
    },
    ...(canApproveDeclinedRequest
      ? [
          {
            id: "approve-declined",
            label: "Approve request",
            leadingIcon: "check-mark" as const,
            onSelect: () => onApprove?.(selectedRequest.id),
          },
        ]
      : []),
    ...(canReview && onDelete && isDeclined
      ? [
          {
            id: "delete",
            label: "Delete request",
            leadingIcon: "trash-delete-bin" as const,
            onSelect: () => onDelete(selectedRequest.id),
            tone: "destructive" as const,
          },
        ]
      : []),
  ];

  return (
    <section
      aria-label={`Request details for ${selectedRequest.title}`}
      className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-(--radius-lg) border border-outline-subtle bg-surface"
    >
      <header className="border-b border-outline-subtle px-(--spacing-24) py-(--spacing-16)">
        <Group
          align={isEditing ? "center" : "start"}
          justify="between"
          wrap="nowrap"
        >
          {isEditing ? (
            <div className="min-w-0 flex-1">
              <Input
                aria-label="Title"
                maxLength={120}
                onChange={(event) => setTitle(event.target.value)}
                reserveSubtextSpace={false}
                value={title}
              />
            </div>
          ) : (
            // Long titles wrap in full; `wrap-anywhere` also breaks a single
            // unbroken word so it never forces horizontal scrolling.
            <div className="min-w-0 flex-1 wrap-anywhere">
              <Group align="start" gap={4} wrap="nowrap">
                <Title as="h2" size="xl">
                  {selectedRequest.title}
                </Title>
                <IconButton
                  aria-label="Copy request link"
                  icon="assign-link"
                  onClick={() => void handleCopyLink()}
                  size="sm"
                  variant="ghost"
                />
              </Group>
            </div>
          )}

          <Group gap={8} wrap="nowrap">
            {isPending && canEditThisRequest ? (
              <Button
                disabled={isEditing}
                onClick={beginEdit}
                size="sm"
                variant="outline"
              >
                Edit
              </Button>
            ) : null}
            <MenuButton
              aria-label="More request actions"
              items={menuItems}
              size="sm"
              variant="overflow"
            />
          </Group>
        </Group>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-(--spacing-24) py-(--spacing-20)">
        <Stack gap={20}>
          {submissionError ? (
            <Alert tone="negative" title={submissionError} />
          ) : null}

          {selectedRequest.status.code === "DECLINED" ? (
            <Alert
              tone={statusDisplay.tone}
              title={`Request declined${selectedRequest.declineReason ? `: ${selectedRequest.declineReason.name}` : ""}`}
              subtext={
                [
                  selectedRequest.reviewer
                    ? `Declined by ${selectedRequest.reviewer.name}`
                    : null,
                  selectedRequest.rejectionReason,
                ]
                  .filter(Boolean)
                  .join(" · ") || undefined
              }
            />
          ) : null}

          <div className="grid gap-(--spacing-24) min-[800px]:grid-cols-2">
            <DetailField label="STATUS">
              <Chip label={statusDisplay.label} tone={statusDisplay.tone} />
            </DetailField>
            {isEditing ? (
              <Select
                label="PRIORITY"
                onChange={(value) =>
                  setPriorityCode(value as RequestPriority | null)
                }
                options={[
                  { label: "High", value: "HIGH" },
                  { label: "Medium", value: "MEDIUM" },
                  { label: "Low", value: "LOW" },
                ]}
                reserveSubtextSpace={false}
                value={priorityCode}
              />
            ) : (
              <DetailField label="PRIORITY">
                <Chip
                  label={
                    selectedRequest.priority?.code === "MEDIUM"
                      ? "Med"
                      : (selectedRequest.priority?.name ?? "Not set")
                  }
                  tone={
                    selectedRequest.priority?.code === "HIGH"
                      ? "negative"
                      : selectedRequest.priority?.code === "MEDIUM"
                        ? "warning"
                        : "neutral"
                  }
                />
              </DetailField>
            )}
          </div>

          {selectedRequest.workOrder ? (
            <>
              <Divider />
              <DetailField label="LINKED WORK ORDER">
                <Tile hasBorder padding="compact" radius="sm" width="full">
                  <Stack gap={4}>
                    <Group gap={8} justify="between" wrap="wrap">
                      {canAccessWorkOrders ? (
                        <Link
                          className="font-(family-name:--font-family-label) text-(length:--font-size-body-medium) font-(number:--font-weight-medium) text-primary underline"
                          params={{
                            workOrderId: selectedRequest.workOrder.id,
                          }}
                          search={{
                            ...scopeSearch,
                            workOrderFullPage: true,
                            ...requestOriginSearch,
                          }}
                          to="/work-orders/$workOrderId"
                        >
                          {workOrderNumberLabel}
                        </Link>
                      ) : (
                        <Text as="span" size="md" weight="medium">
                          {workOrderNumberLabel}
                        </Text>
                      )}
                      <Chip
                        label={selectedRequest.workOrder.status.name}
                        tone={
                          workOrderStatusTone[workOrderStatusCode] ?? "neutral"
                        }
                      />
                    </Group>
                    <Text as="span" size="lg" weight="medium">
                      {selectedRequest.workOrder.title}
                    </Text>
                    <Text
                      as="span"
                      data-testid="linked-work-order-updated"
                      size="sm"
                      tone="subtle"
                    >
                      Last updated {formatCompactDateTime(workOrderUpdatedAt)}
                    </Text>
                  </Stack>
                </Tile>
              </DetailField>
            </>
          ) : null}

          {showLocationAssetSection ? <Divider /> : null}

          <div
            className="grid gap-(--spacing-24) min-[800px]:grid-cols-2"
            hidden={!showLocationAssetSection}
          >
            {isEditing ? (
              // The New Request picker covers both assets and locations, and
              // the request's location is derived from the chosen asset, so
              // edit mode shows a single full-width picker in place of both.
              <div className="min-[800px]:col-span-2">
                <RequestAssetPicker
                  label="ASSET"
                  onChange={(id) => setAssetId(id)}
                  value={assetId}
                />
              </div>
            ) : viewLocationName ? (
              <DetailField label="LOCATION">
                <span className="font-(family-name:--font-family-body) text-(length:--font-size-body-large) leading-(--font-line-height-24) text-primary">
                  {viewLocationName}
                </span>
              </DetailField>
            ) : null}
            {isEditing ? null : selectedRequest.asset ? (
              <DetailField label="ASSET">
                <div className="flex min-w-0 items-center gap-(--spacing-12) wrap-anywhere">
                  <span className="inline-flex size-(--spacing-40) shrink-0 items-center justify-center rounded-full bg-surface-container-low">
                    <Icon name="asset" size="sm" tone="brand" />
                  </span>
                  <Text as="span" size="lg">
                    {canLinkAsset ? (
                      <Link
                        className="box-decoration-clone rounded-(--radius-xs) text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-button-focus-ring"
                        params={{ assetId: selectedRequest.asset.publicId }}
                        search={{ ...scopeSearch, ...requestOriginSearch }}
                        to="/assets/$assetId"
                      >
                        {selectedRequest.asset.name}
                      </Link>
                    ) : (
                      selectedRequest.asset.name
                    )}
                  </Text>
                </div>
              </DetailField>
            ) : null}
          </div>

          {showDescriptionSection ? <Divider /> : null}

          {isEditing ? (
            <Textarea
              label="DESCRIPTION"
              onChange={(event) => setDescription(event.currentTarget.value)}
              value={description}
            />
          ) : viewDescription ? (
            <DetailField label="DESCRIPTION">
              <Text>{viewDescription}</Text>
            </DetailField>
          ) : null}

          {showDescriptionSection || showLocationAssetSection ? (
            <Divider />
          ) : null}

          <RequestAttachments requestId={selectedRequest.id} />

          <Divider />

          {isPending ? (
            <Group justify="between" wrap="wrap">
              <PersonMetadata
                label="Requested By"
                name={selectedRequest.requester.name}
              />
              <Text as="span" size="sm" tone="subtle">
                {formatCompactDateTime(selectedRequest.submittedAt)}
              </Text>
            </Group>
          ) : (
            <Stack gap={8}>
              <Group justify="between" wrap="wrap">
                <PersonMetadata
                  label="Requested By"
                  name={selectedRequest.requester.name}
                />
                <Text as="span" size="sm" tone="subtle">
                  {formatCompactDateTime(selectedRequest.submittedAt)}
                </Text>
              </Group>
              {reviewerName ? (
                <PersonMetadata label="Reviewed by" name={reviewerName} />
              ) : null}
              <Group justify="between" wrap="wrap">
                <PersonMetadata label="Last updated By" name={lastUpdatedBy} />
                <Text as="span" size="sm" tone="subtle">
                  {formatDateTime(selectedRequest.updatedAt)}
                </Text>
              </Group>
            </Stack>
          )}
        </Stack>
        <div className="flex justify-end pt-(--spacing-16)">
          {isEditing ? (
            <Group justify="end" wrap="wrap">
              <Button
                disabled={updateMutation.isPending}
                onClick={() => setIsEditing(false)}
                size="sm"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={title.trim().length === 0}
                loading={updateMutation.isPending}
                loadingText="Updating request"
                onClick={() => void handleUpdate()}
                size="sm"
              >
                Update Request
              </Button>
            </Group>
          ) : isReviewable ? (
            <Group justify="end" wrap="wrap">
              {isPending && canCancelThisRequest ? (
                <Button
                  intent="destructive"
                  onClick={openCancelConfirmation}
                  size="sm"
                >
                  Cancel Request
                </Button>
              ) : null}
              {canReview ? (
                <>
                  <Button
                    onClick={openDeclineConfirmation}
                    size="sm"
                    variant="outline"
                  >
                    Decline Request
                  </Button>
                  <Button
                    onClick={() => onApprove?.(selectedRequest.id)}
                    size="sm"
                  >
                    Approve Request
                  </Button>
                </>
              ) : null}
            </Group>
          ) : (
            <Group justify="end" wrap="wrap">
              <Text
                size="sm"
                tone={
                  statusDisplay.tone === "neutral"
                    ? "subtle"
                    : statusDisplay.tone
                }
              >
                {statusDisplay.label} {"\u00b7"}{" "}
                {formatCompactDateTime(
                  selectedRequest.reviewedAt ?? selectedRequest.updatedAt,
                )}
              </Text>
            </Group>
          )}
        </div>
      </div>

      <DeclineRequestDialog
        error={declineError}
        isPending={declineMutation.isPending}
        onConfirm={(input) => void handleDecline(input)}
        onOpenChange={handleDeclineOpenChange}
        open={isDeclineOpen}
        requestNumber={selectedRequest.requestNumber}
      />
      <CancelRequestDialog
        error={cancelError}
        isPending={cancelMutation.isPending}
        onConfirm={() => void handleCancel()}
        onOpenChange={handleCancelOpenChange}
        open={isCancelOpen}
        requestNumber={selectedRequest.requestNumber}
        requestTitle={selectedRequest.title}
      />
    </section>
  );
}
